import { getOSInfoObj } from '../user-env';

/** 常量 缓存key 默认字体缩放比例 */
const CACHE_KEY = 'CACHE_FOR_FONT_INFO';
// 不同机型上，fontscale的返回值范围是不一样的，但是标准值固定是100
const DEFAULT_FONT_SCALE = 100;

const enum VISIBILITY {
  VISIBILITY_HIDDEN = 'hidden',
  VISIBILITY_VISIBLE = 'visible',
}

export interface FontInfo {
  /** 字体放大比例 */
  fontScale: number;
  /** 放大等级 */
  fontLevel: number;
}

// 获取微信字体大小
export async function getFontInfo(): Promise<FontInfo | undefined> {
  const isReady = await isWxBridgeReady();
  // 超时
  if (!isReady) {
    return;
  }

  const font = getFontInfoWxApi();
  if (!font) {
    return;
  }
  try {
    const ret = JSON.parse(font);
    console.log('JSON.parse getEnv获取成功', ret);
    return {
      fontLevel: Number(ret.fontLevel),
      fontScale: Number(ret.fontScale),
    };
  } catch (err) {
    console.log('JSON.parse getEnv获取失败', err);
    return;
  }
};

/**
 * 处理ios上的大字体闪动问题。
 */
export async function handleBigFont() {
  // 不是ios不执行
  if (!checkIsIOS()) return;

  // 如果有缓存，先以缓存展示
  const fontScaleCache = getFontInfoInCache();
  if (fontScaleCache) {
    setBodyTextSize(fontScaleCache);
  } else {
    // 页面先不可见
    setBodyVisibility(VISIBILITY.VISIBILITY_HIDDEN);
  }

  // 获取字体信息
  const fontInfo = await getFontInfo();
  if (!fontInfo) {
    // 如果没有获取到微信字体信息，就是用缓存或默认信息
    setAndShowPage(fontScaleCache || DEFAULT_FONT_SCALE);
  } else {
    // 拿到字体信息后，展示并缓存一下
    setFontInfoInCache(fontInfo.fontScale);
    setAndShowPage(fontInfo.fontScale);
  }
}

export async function isWxBridgeReady(timeout = 1500) {
  let isWeixinBridgeReady = false;
  return new Promise((res) => {
    // 已经初始化
    if (window.__wxWebEnv) {
      return res(true);
    }
    // 未初始化
    document.addEventListener('WeixinJSBridgeReady', () => {
      isWeixinBridgeReady = true;
      return res(true);
    }, false);
    // 监听超时
    if (timeout > 0) {
      // 避免WeixinJSBridgeReady事件不触发
      setTimeout(() => {
        if (!isWeixinBridgeReady) {
          return res(false);
        }
      }, timeout);
    };
  });
}

// 微信webview内提供的获取字体信息的api
export function getFontInfoWxApi() {
  return window.__wxWebEnv?.getEnv();
}

export function checkIsIOS() {
  // 不是iPhone不处理
  const { type } = getOSInfoObj();
  return type === 'iphone';
}

export function setAndShowPage(fontScale: number) {
  setBodyTextSize(fontScale);
  setBodyVisibility(VISIBILITY.VISIBILITY_VISIBLE);
}

export function setBodyTextSize(fontScale: number) {
  document.body.style.webkitTextSizeAdjust = `${fontScale}%`;
}

export function setBodyVisibility(visibility: VISIBILITY) {
  document.body.style.visibility = visibility;
}

/**
 * 获取缓存中的字体信息
 */
export function getFontInfoInCache(): number | null {
  const fontScale = localStorage.getItem(CACHE_KEY);
  return fontScale ? Number(fontScale) : null;
}

// 缓存字体信息
export function setFontInfoInCache(fontScale: number) {
  localStorage.setItem(CACHE_KEY, String(fontScale));
}

/** 通过css设置body text size adjust */
export const setBodyTextSizeByCss = (fontScale: number) => {
  const styleElement = document.createElement('style');
  styleElement.innerHTML = `
    body {
      -webkit-text-size-adjust: ${fontScale}% !important;
    }`;
  document.head.appendChild(styleElement);
};

/**
 * 禁用整个页面字体缩放
 * 可用于IOS和Android
*/
export const disableFontSizeScale = async () => {
  try {
    if (checkIsIOS()) {
      // ios处理：加样式即可.注意：要通过css加样式(且加!important)，而不是style，因为后面WeixinJSBridge会通过style进行setFont，把当前的设置覆盖掉
      setBodyTextSizeByCss(DEFAULT_FONT_SCALE);
    } else {
      // Android处理：需要用WeixinJSBridge.invoke
      if (typeof window.WeixinJSBridge === 'object' && typeof window.WeixinJSBridge.invoke === 'function') {
        handleFontSize();
      } else {
        if (document.addEventListener) {
          document.addEventListener('WeixinJSBridgeReady', handleFontSize, false);
        }
      }
      function handleFontSize() {
        window.WeixinJSBridge!.invoke('setFontSizeCallback', { fontSize: 0 });
        window.WeixinJSBridge!.on('menu:setfont', () => {
          window.WeixinJSBridge!.invoke('setFontSizeCallback', { fontSize: 0 });
        });
      }
    }
  } catch (e) { }
};

